import { type FC } from "react";
import { Info } from "lucide-react";

import { Checkbox } from "@/shared/components/ui/checkbox";
import { SelectableChip } from "@/shared/components/ui/SelectableChip";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { BALANCE_ESTIMATE_RANGES } from "@/shared/constants/balanceEstimates";

interface BalanceEstimateSectionProps {
  balanceUnknown: boolean;
  estimateRange: string;
  onBalanceUnknownChange: (checked: boolean) => void;
  onEstimateRangeChange: (key: string) => void;
  firstName?: string;
}

export const BalanceEstimateSection: FC<BalanceEstimateSectionProps> = ({
  balanceUnknown,
  estimateRange,
  onBalanceUnknownChange,
  onEstimateRangeChange,
  firstName,
}) => {
  return (
    <div className="space-y-4">
      <label className="flex cursor-pointer items-center gap-2">
        <Checkbox
          checked={balanceUnknown}
          onCheckedChange={(checked) => onBalanceUnknownChange(checked === true)}
        />
        <span className="text-sm font-medium text-secondary">I don't know the balance yet</span>
      </label>

      {balanceUnknown && (
        <div className="space-y-2 pl-6">
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium text-secondary">Estimated range</p>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="More info" className="inline-flex">
                    <Info className="h-3.5 w-3.5 text-tertiary" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[220px]">
                  <p className="text-sm">
                    Even a rough estimate helps us shape the right roadmap for settling{" "}
                    {firstName ? `${firstName}'s` : "the"} estate
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex flex-wrap gap-2">
            {BALANCE_ESTIMATE_RANGES.map((range) => (
              <SelectableChip
                key={range.key}
                label={range.label}
                selected={estimateRange === range.key}
                onClick={() => onEstimateRangeChange(range.key)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
